/**
 * Events List Page (Admin)
 *
 * Displays a list of all events with:
 * - Filtering by status, date range, search
 * - Pagination
 * - Links to create/edit events
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  listEvents,
  deleteEvent,
  updateEvent,
  Event,
  EventStatus,
  ListEventsParams,
  UpdateEventInput,
} from '../../api/events';
import { getErrorMessage } from '../../api/client';
import { CSVImportModal } from '../../components/events/CSVUpload';
import AddRoleModal from '../../components/events/AddRoleModal';
import BulkUpdateTimesModal, { type BulkTimes } from '../../components/events/BulkUpdateTimesModal';
import { ConfirmDialog } from '../../components/ui';
import {
  combineEventDateWithTime,
  formatDate,
  formatTime,
  startOfToday,
} from '../../utils/dateFormatting';

/** "1 Event" / "6 Events" — used across the bulk confirmation dialogs. */
const eventCount = (n: number) => `${n} Event${n === 1 ? '' : 's'}`;

type FilterStatus = EventStatus | 'ALL' | 'UPCOMING';

export default function EventsPage() {
  const navigate = useNavigate();

  // State
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('UPCOMING');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Denominator for the Responses column: the org's active crew roster. One
  // org-wide number, so the API returns it beside the pagination block rather
  // than repeating it on every row.
  const [activeCrewTotal, setActiveCrewTotal] = useState(0);

  // Bulk update mode (owners/admins). Selected events are kept as full
  // objects (not just ids) so cross-page selections survive pagination and
  // the times update can read each event's own date.
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<Map<string, Event>>(new Map());
  const [bulkAction, setBulkAction] = useState<'delete' | 'roles' | 'times' | null>(null);
  const [pendingTimes, setPendingTimes] = useState<BulkTimes | null>(null);
  const [isBulkWorking, setIsBulkWorking] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ListEventsParams = {
        page,
        limit,
      };

      if (statusFilter === 'UPCOMING') {
        params.startDate = startOfToday().toISOString();
      } else if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const response = await listEvents(params);
      setEvents(response.events);
      setActiveCrewTotal(response.activeCrewTotal);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Handle status filter change
  const handleStatusChange = (status: FilterStatus) => {
    setStatusFilter(status);
    setPage(1); // Reset to first page
  };

  const clearSelection = () => {
    setSelected(new Map());
    setBulkAction(null);
    setPendingTimes(null);
  };

  const exitBulkMode = () => {
    setBulkMode(false);
    clearSelection();
  };

  const toggleSelect = (event: Event) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(event.id)) {
        next.delete(event.id);
      } else {
        next.set(event.id, event);
      }
      return next;
    });
  };

  const selectPage = () => {
    setSelected(prev => {
      const next = new Map(prev);
      events.forEach(e => next.set(e.id, e));
      return next;
    });
  };

  /**
   * Run one operation per selected event, tolerate partial failure, and
   * report the outcome as a toast. The list is refetched either way.
   * Selection spans pages, so the fan-out is chunked — a 200-event delete
   * must not become 200 simultaneous requests.
   */
  const runBulk = async (label: string, op: (event: Event) => Promise<unknown>) => {
    const targets = [...selected.values()];
    const CHUNK = 8;
    setIsBulkWorking(true);
    try {
      let failed = 0;
      for (let i = 0; i < targets.length; i += CHUNK) {
        const results = await Promise.allSettled(targets.slice(i, i + CHUNK).map(op));
        failed += results.filter(r => r.status === 'rejected').length;
      }
      if (failed === 0) {
        toast.success(`${targets.length} event${targets.length === 1 ? '' : 's'} ${label}`);
      } else {
        toast.error(
          `${targets.length - failed} of ${targets.length} events ${label}; ${failed} failed`
        );
      }
      clearSelection();
      fetchEvents();
    } finally {
      setIsBulkWorking(false);
    }
  };

  const handleBulkDelete = () => runBulk('deleted', event => deleteEvent(event.id));

  const handleBulkTimes = () => {
    if (!pendingTimes) return;
    return runBulk('updated', event => {
      // Same wall-clock time on each event's own calendar day.
      const payload: UpdateEventInput = {};
      if (pendingTimes.callTime) payload.callTime = combineEventDateWithTime(event.eventDate, pendingTimes.callTime);
      if (pendingTimes.startTime) payload.startTime = combineEventDateWithTime(event.eventDate, pendingTimes.startTime);
      if (pendingTimes.endTime) payload.endTime = combineEventDateWithTime(event.eventDate, pendingTimes.endTime);
      return updateEvent(event.id, payload);
    });
  };

  const selectedCount = selected.size;

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-gray-600 mt-1">
            Manage broadcast events and crew assignments
          </p>
        </div>
        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            onClick={() => (bulkMode ? exitBulkMode() : setBulkMode(true))}
            className={`inline-flex items-center px-4 py-2 border rounded-lg transition-colors ${
              bulkMode
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {bulkMode ? 'Done' : 'Bulk Update'}
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import CSV
          </button>
          <Link
            to="/admin/events/new"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Event
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <label htmlFor="search" className="sr-only">Search events</label>
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                id="search"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack"
              />
            </div>
          </div>

          {/* Status filter */}
          <div className="sm:w-48">
            <label htmlFor="status" className="sr-only">Filter by status</label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value as FilterStatus)}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack"
            >
              <option value="UPCOMING">Upcoming</option>
              <option value="ALL">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PUBLISHED">Published</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {bulkMode && (
        <div className="mb-6 flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          <span className="text-sm font-medium text-gray-700">{selectedCount} selected</span>
          <button
            type="button"
            onClick={selectPage}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Select page
          </button>
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={() => setSelected(new Map())}
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            disabled={selectedCount === 0 || isBulkWorking}
            onClick={() => setBulkAction('roles')}
            className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md disabled:opacity-50"
          >
            Add Roles
          </button>
          <button
            type="button"
            disabled={selectedCount === 0 || isBulkWorking}
            onClick={() => setBulkAction('times')}
            className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md disabled:opacity-50"
          >
            Update Times
          </button>
          <button
            type="button"
            disabled={selectedCount === 0 || isBulkWorking}
            onClick={() => setBulkAction('delete')}
            className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Events table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {debouncedSearch || (statusFilter !== 'ALL' && statusFilter !== 'UPCOMING')
                ? 'Try adjusting your filters'
                : 'Get started by creating a new event'}
            </p>
            {!debouncedSearch && (statusFilter === 'ALL' || statusFilter === 'UPCOMING') && (
              <Link
                to="/admin/events/new"
                className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Create Event
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {bulkMode && (
                      <th className="pl-6 pr-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Select
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Crew
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responses
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {events.map((event) => {
                    const crew = event.crew || {
                      assigned: 0,
                      confirmed: 0,
                      pending: 0,
                      needed: 0,
                      responded: null,
                    };
                    const allConfirmed = crew.assigned > 0 && crew.confirmed === crew.assigned;
                    const hasPending = crew.pending > 0;
                    const needsMore = crew.assigned < crew.needed;

                    return (
                      <tr
                        key={event.id}
                        onClick={() =>
                          bulkMode ? toggleSelect(event) : navigate(`/admin/events/${event.id}`)
                        }
                        className={`hover:bg-gray-50 cursor-pointer ${
                          bulkMode && selected.has(event.id) ? 'bg-primary-50' : ''
                        }`}
                      >
                        {bulkMode && (
                          <td className="pl-6 pr-2 py-4 whitespace-nowrap">
                            <SelectCheckbox
                              event={event}
                              checked={selected.has(event.id)}
                              onToggle={toggleSelect}
                            />
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <div>
                            <span className="font-medium text-gray-900">
                              {event.name}
                            </span>
                            <p className="text-sm text-gray-500">{event.venue}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(event.eventDate)}
                          </div>
                          {(event.callTime || event.startTime) && (
                            <div className="text-sm text-gray-500">
                              {event.callTime && `Call: ${formatTime(event.callTime)}`}
                              {event.callTime && event.startTime && ' · '}
                              {event.startTime && `Start: ${formatTime(event.startTime)}`}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={event.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <span className={needsMore ? 'text-yellow-600' : 'text-gray-900'}>
                              {crew.assigned}/{crew.needed} assigned
                            </span>
                          </div>
                          {crew.assigned > 0 && (
                            <div className="text-xs mt-0.5">
                              {allConfirmed ? (
                                <span className="text-green-600">All confirmed</span>
                              ) : (
                                <span className={hasPending ? 'text-blue-600' : 'text-gray-500'}>
                                  {crew.confirmed} confirmed{hasPending && `, ${crew.pending} awaiting`}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {crew.responded === null ? (
                            <span
                              className="text-sm text-gray-500"
                              title="Crew cannot see a draft, so there is nothing for them to answer yet"
                            >
                              n/a
                            </span>
                          ) : (
                            <>
                              <div className="text-sm text-gray-900">
                                {crew.responded}/{activeCrewTotal}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                set availability
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {events.map((event) => {
                const crew = event.crew || {
                  assigned: 0,
                  confirmed: 0,
                  pending: 0,
                  needed: 0,
                  responded: null,
                };
                const allConfirmed = crew.assigned > 0 && crew.confirmed === crew.assigned;
                const hasPending = crew.pending > 0;
                const needsMore = crew.assigned < crew.needed;

                return (
                  <div
                    key={event.id}
                    onClick={() =>
                      bulkMode ? toggleSelect(event) : navigate(`/admin/events/${event.id}`)
                    }
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      bulkMode && selected.has(event.id) ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      {bulkMode && (
                        <SelectCheckbox
                          event={event}
                          checked={selected.has(event.id)}
                          onToggle={toggleSelect}
                          className="mr-3 mt-0.5 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">
                          {event.name}
                        </span>
                        <p className="text-sm text-gray-500">{event.venue}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {formatDate(event.eventDate)}
                          {event.startTime && ` at ${formatTime(event.startTime)}`}
                        </p>
                      </div>
                      <StatusBadge status={event.status} />
                    </div>
                    <div className="mt-2 text-sm">
                      <span className={needsMore ? 'text-yellow-600' : 'text-gray-500'}>
                        {crew.assigned}/{crew.needed} assigned
                      </span>
                      {crew.assigned > 0 && (
                        <span className="ml-2">
                          {allConfirmed ? (
                            <span className="text-green-600">• All confirmed</span>
                          ) : (
                            <span className={hasPending ? 'text-blue-600' : 'text-gray-500'}>
                              • {crew.confirmed} confirmed{hasPending && `, ${crew.pending} awaiting`}
                            </span>
                          )}
                        </span>
                      )}
                      {crew.responded !== null && (
                        <span className="ml-2 text-gray-500">
                          • {crew.responded}/{activeCrewTotal} responded
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} events
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={fetchEvents}
      />

      {/* Bulk add roles — the one bulk action with no type-to-confirm gate */}
      <AddRoleModal
        isOpen={bulkAction === 'roles'}
        onClose={() => setBulkAction(null)}
        eventIds={[...selected.keys()]}
        onRoleAdded={fetchEvents}
      />

      {/* Bulk update times: collect times, then confirm by typing the count */}
      <BulkUpdateTimesModal
        isOpen={bulkAction === 'times' && !pendingTimes}
        onClose={() => setBulkAction(null)}
        onSubmit={setPendingTimes}
        eventCount={selectedCount}
      />
      <ConfirmDialog
        isOpen={bulkAction === 'times' && !!pendingTimes}
        onClose={() => setPendingTimes(null)}
        onConfirm={handleBulkTimes}
        title={`Update ${eventCount(selectedCount)}`}
        message={`This changes the times on ${eventCount(selectedCount).toLowerCase()}. Crew assigned to visible events will be notified of the change.`}
        requiredText={String(selectedCount)}
        confirmLabel="Update"
        isLoading={isBulkWorking}
        variant="default"
      />

      {/* Bulk delete: confirm by typing the count */}
      <ConfirmDialog
        isOpen={bulkAction === 'delete'}
        onClose={() => setBulkAction(null)}
        onConfirm={handleBulkDelete}
        title={`Delete ${eventCount(selectedCount)}`}
        message={`This permanently deletes ${eventCount(selectedCount).toLowerCase()} and all their roles and assignments. This action cannot be undone.`}
        requiredText={String(selectedCount)}
        confirmLabel="Delete"
        isLoading={isBulkWorking}
        variant="danger"
      />
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface SelectCheckboxProps {
  event: Event;
  checked: boolean;
  onToggle: (event: Event) => void;
  className?: string;
}

/** Bulk-mode selection checkbox, shared by the desktop table and mobile cards. */
function SelectCheckbox({ event, checked, onToggle, className = '' }: SelectCheckboxProps) {
  return (
    <input
      type="checkbox"
      aria-label={`Select ${event.name}`}
      checked={checked}
      onChange={() => onToggle(event)}
      onClick={(e) => e.stopPropagation()}
      className={`h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-rack cursor-pointer ${className}`}
    />
  );
}

interface StatusBadgeProps {
  status: EventStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const styles: Record<EventStatus, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    PUBLISHED: 'bg-green-100 text-green-800',
    COMPLETED: 'bg-blue-100 text-blue-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };

  const labels: Record<EventStatus, string> = {
    DRAFT: 'Draft',
    PUBLISHED: 'Published',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  };

  return (
    <span className={`px-2.5 py-0.5 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}
